import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import styled from 'styled-components';
import { getDemandTypeLabel } from '../../services/demandTypeService';
import ChartCard, { ChartLegend, ChartTooltip, TruncatedTick } from './ChartCard';
import {
  ANIMATION,
  AXIS_TICK,
  MOBILE_MQ,
  axisProps,
  demandTypeColor,
  formatNumber,
  gridProps,
  toCount,
  useChartAnimation,
  useMediaQuery,
} from './chartTheme';

const ChartScroll = styled.div`
  width: 100%;
  max-height: 640px;
  overflow: auto;
`;

const ROW_HEIGHT = 44;

const ALL_DEMAND_TYPE_KEYS = ['SUPPORT', 'IMPROVEMENT', 'PROJECT', 'OTHER'];

const orderedKeys = (keys) =>
  ALL_DEMAND_TYPE_KEYS.filter((key) => keys.includes(key));

const getCreatorName = (row) =>
  String(row?.responsibleUserName ?? row?.createdUserName ?? row?.createdUser ?? '').trim();

const pivotBreakdownRows = (rows) =>
  rows
    .map((row) => {
      const name = getCreatorName(row);
      if (!name) return null;

      const bucket = { name, total: toCount(row?.count) };
      const breakdown = Array.isArray(row?.byDemandType) ? row.byDemandType : [];
      for (const item of breakdown) {
        const type = String(item?.demandType || '').toUpperCase();
        if (!type) continue;
        bucket[type] = (bucket[type] ?? 0) + toCount(item?.count);
      }
      return bucket;
    })
    .filter(Boolean)
    .sort((a, b) => b.total - a.total);

const TotalByCreatedByUser = ({
  data = [],
  loading = false,
  error = false,
  onRetry = null,
  demandTypeFilter = [],
}) => {
  const isMobile = useMediaQuery(MOBILE_MQ);
  const animate = useChartAnimation();
  const [activeKey, setActiveKey] = useState(null);

  const seriesKeys = useMemo(() => {
    if (Array.isArray(demandTypeFilter) && demandTypeFilter.length > 0) {
      return orderedKeys(demandTypeFilter.map((t) => String(t).toUpperCase()));
    }
    return ALL_DEMAND_TYPE_KEYS;
  }, [demandTypeFilter]);

  const chartData = useMemo(
    () => pivotBreakdownRows(Array.isArray(data) ? data : []),
    [data]
  );

  const total = useMemo(() => chartData.reduce((sum, row) => sum + row.total, 0), [chartData]);

  const legendItems = seriesKeys.map((key, index) => ({
    key,
    label: getDemandTypeLabel(key),
    color: demandTypeColor(key, index),
  }));

  const chartHeight = Math.max(240, chartData.length * ROW_HEIGHT + 32);

  return (
    <ChartCard
      title="Tickets por usuário criador"
      description="Quem mais abre tickets, por tipo de demanda"
      stat={total}
      statLabel="tickets criados"
      loading={loading}
      error={error}
      onRetry={onRetry}
      isEmpty={chartData.length === 0}
      emptyTitle="Nenhum ticket encontrado"
      emptyDescription="Ajuste o filtro de tipo de demanda para ver resultados."
      skeleton="bars"
    >
      {seriesKeys.length > 1 ? (
        <ChartLegend
          items={legendItems}
          activeKey={activeKey}
          onActiveChange={setActiveKey}
          label="Tipos de demanda"
        />
      ) : null}
      <ChartScroll>
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart
            layout="vertical"
            data={chartData}
            margin={{ top: 0, right: 44, left: 0, bottom: 0 }}
            barCategoryGap="28%"
          >
            <CartesianGrid {...gridProps} horizontal={false} />
            <XAxis type="number" allowDecimals={false} domain={[0, 'auto']} {...axisProps} />
            <YAxis
              type="category"
              dataKey="name"
              width={isMobile ? 112 : 176}
              interval={0}
              axisLine={false}
              tickLine={false}
              tick={<TruncatedTick maxChars={isMobile ? 14 : 24} />}
            />
            <Tooltip
              cursor={{ fill: 'var(--chart-cursor)' }}
              content={<ChartTooltip nameFormatter={getDemandTypeLabel} showTotal hideZero />}
            />
            {seriesKeys.map((key, index) => (
              <Bar
                key={key}
                dataKey={key}
                name={key}
                stackId="demandType"
                fill={demandTypeColor(key, index)}
                fillOpacity={activeKey && activeKey !== key ? 0.25 : 1}
                radius={index === seriesKeys.length - 1 ? [0, 4, 4, 0] : 0}
                maxBarSize={20}
                isAnimationActive={animate}
                {...ANIMATION}
              >
                {index === seriesKeys.length - 1 ? (
                  <LabelList
                    dataKey="total"
                    position="right"
                    offset={8}
                    formatter={formatNumber}
                    style={{ ...AXIS_TICK, fill: 'var(--color-text-secondary)', fontWeight: 600 }}
                  />
                ) : null}
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      </ChartScroll>
    </ChartCard>
  );
};

TotalByCreatedByUser.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      responsibleUserName: PropTypes.string,
      createdUserName: PropTypes.string,
      createdUser: PropTypes.string,
      count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      byDemandType: PropTypes.arrayOf(
        PropTypes.shape({
          demandType: PropTypes.string,
          count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        })
      ),
    })
  ),
  loading: PropTypes.bool,
  error: PropTypes.bool,
  onRetry: PropTypes.func,
  demandTypeFilter: PropTypes.arrayOf(PropTypes.string),
};

export default TotalByCreatedByUser;
